<Transition name="bcx-loader-slice">
    <BolideLoader v-if="is_loading" />
</Transition>

<div class="dashboard-container container-fluid px-0" v-show="!is_loading">
    <div class="row g-0 vh-100 overflow-hidden">

        <!-- ======= SIDEBAR AREA (DESKTOP ONLY) ======= -->
        <div v-if="!is_mobile"
            class="col-auto sidebar-wrapper sidebar-offset d-flex flex-column flex-shrink-0 position-sticky top-0 vh-100 overflow-hidden overflow-x-visible"
            :class="{ 'sidebar-collapsed': sidebar_collapsed }">
            <!-- DESKTOP SIDEBAR SCROLL AREA -->
            <div class="h-100 overflow-auto overflow-x-hidden no-scrollbar">
                <!-- ===== PORTFOLIO MODE ===== -->
                <PortfolioSidebar v-if="!is_property_view && can_view_portfolio" :tiles="tiles"
                    :current_active_property="selected_property?.title" :active_item="active_menu_item"
                    @tile-clicked="handleTileClick" @global-menu-selected="handleGlobalMenuSelected"
                    @property-menu-selected="handlePropertyMenuSelected" @global-selected="goBack" />

                <!-- ===== PROPERTY MODE ===== -->
                <PropertySidebar v-else-if="selected_property" :active_property="selected_property" :properties="tiles"
                    :active_item="active_menu_item" :can_view_portfolio="can_view_portfolio"
                    @switch-property="handleTileClick" @go-back="goBack"
                    @property-menu-selected="handlePropertyMenuSelected" />
            </div>
        </div>

        <!-- ======= MOBILE SIDEBAR OVERLAY ======= -->
        <div v-if="is_mobile && mobile_sidebar_open" class="mobile-sidebar-overlay" @click.self="close_sidebar">
            <div class="mobile-sidebar-panel h-100 overflow-hidden">
                <div class="h-100 overflow-auto overflow-x-hidden">
                    <!-- ===== PORTFOLIO MODE ===== -->
                    <PortfolioSidebar v-if="!is_property_view && can_view_portfolio" :tiles="tiles"
                        :current_active_property="selected_property?.title" :active_item="active_menu_item"
                        @tile-clicked="handleTileClick" @global-menu-selected="handleGlobalMenuSelected"
                        @property-menu-selected="handlePropertyMenuSelected" @global-selected="goBack"
                        @close-sidebar="close_sidebar">
                        <template #sidebar-header-actions>
                            <button class="sidebar-close-btn" @click="close_sidebar" aria-label="Close sidebar">
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </template>
                    </PortfolioSidebar>

                    <!-- ===== PROPERTY MODE ===== -->
                    <PropertySidebar v-else-if="selected_property" :active_property="selected_property"
                        :properties="tiles" :active_item="active_menu_item"
                        :can_view_portfolio="can_view_portfolio" @switch-property="handleTileClick"
                        @go-back="goBack" @property-menu-selected="handlePropertyMenuSelected"
                        @close-sidebar="close_sidebar">
                        <template #sidebar-header-actions>
                            <button class="sidebar-close-btn" @click="close_sidebar" aria-label="Close sidebar">
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </template>
                    </PropertySidebar>
                </div>
            </div>
        </div>

        <!-- ======= SIDEBAR COLLAPSE BUTTON ======= -->
        <button v-if="!is_mobile" class="sidebar-collapse-btn btn btn-sm btn-dark float-start" @click="toggleSidebar">
            <i :class="sidebar_collapsed ? 'bi bi-chevron-double-right' : 'bi bi-chevron-double-left'"></i>
        </button>

        <!-- ======= MAIN CONTENT AREA ======= -->
        <div class="col vh-100 d-flex flex-column overflow-hidden no-scrollbar">
            <!-- HEADER -->
            <HeaderSection :breadcrumb="breadcrumb" @breadcrumb-click="handleBreadcrumbClick"
                @open-assistant="showAssistant = true" @toggle-mobile-sidebar="toggle_mobile_sidebar">
            </HeaderSection>

            <BolideAssistant
                :is_open="showAssistant"
                :scope="assistant_scope"
                :compare_property_options="comparison_property_options"
                :compare_property_ids="selected_compare_property_ids"
                @update:compare_property_ids="selected_compare_property_ids = $event"
                @close-assistant="showAssistant = false"
            />

            <!-- MAIN SCROLL AREA -->
            <main
                class="dashboard-main flex-grow-1 overflow-auto overflow-x-hidden"
                :class="{ 'dashboard-main-assist-open': showAssistant }"
            >
                <!-- ================= MAIN VIEW (GLOBAL) ================= -->
                <div v-if="currentView === 'main'" class="dashboard-main-view h-100">
                    <div class="row g-0 h-100">
                        <PortfolioSuperSetChart :content_class="getMainContentClass()" chart_id="dashboard-main"
                            :iframe_loading="iframeLoading" />
                    </div>
                </div>

                <div v-else-if="currentView === 'no-assigned-properties'" class="d-flex align-items-center justify-content-center h-100 p-4">
                    <div class="card bcx-card text-center p-4">
                        <h5 class=" fw-bold mb-2">No properties assigned</h5>
                        <p class="text-secondary text-xs mb-0">
                            Ask an administrator to assign properties to this user.
                        </p>
                    </div>
                </div>

                <!-- ============== PROPERTY VIEW ============== -->
                <div v-else-if="is_property_view && !isDetailView(currentView)"
                    class="dashboard-property-view h-100 d-flex flex-column">
                    <!-- PROPERTY OVERVIEW -->
                    <div class="mb-3">
                        <PropertyOverview v-if="selected_property" :centre="selected_property.propertyOverview || {
                                title: selected_property.title,
                                size: 'N/A',
                                occupancy: 'N/A',
                                description: selected_property.description || 'Property'
                            }" :total_centres="selected_property.propertyOverview?.total_centres || tiles.length" />
                    </div>

                    <!-- PROPERTY CONTENT SCROLL AREA -->
                    <div class="property-dashboard-shell flex-grow-1 overflow-auto overflow-x-hidden pe-1">
                        <div class="card rounded-3 bg-transparent border-0 h-100">
                            <div
                                class="property-dashboard-content p-3 h-100"
                                :class="{
                                    'property-resources-background': active_menu_item === 'resources',
                                    'operations-dashboard-content': active_menu_item === 'operations-overview'
                                }"
                            >
                                <!-- ❗ SHOW SUPERSET ONLY WHEN NOT ON RESOURCES -->
                                <template v-if="active_menu_item !== 'resources'">
                                    <!-- Superset Dashboard -->
                                    <div
                                        class="portfolio-chart property-portfolio-chart mb-0"
                                        :class="{ 'operations-superset-panel': active_menu_item === 'operations-overview' }"
                                    >
                                        <div
                                            class="superset-embedded-container property-superset-container m-0 p-0"
                                            :class="{ 'operations-superset-container': active_menu_item === 'operations-overview' }"
                                        >
                                            <div :id="'dashboard-' + currentView"></div>

                                            <div v-if="iframeLoading" class="iframe-loading text-center py-3">
                                                <div class="spinner-border spinner-border-sm me-2"></div>
                                                Loading embedded dashboard...
                                            </div>

                                            <div v-if="iframeError" class="error-message mt-2 text-danger text-center">
                                                Failed to load dashboard. Please try refreshing.
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Emergency / Operations Overview Extras -->
                                    <template v-if="active_menu_item === 'operations-overview'">
                                        <div class="d-flex flex-column gap-3 pb-4">
                                            <template v-for="tileCard in getTileCards()" :key="tileCard.id">
                                                <EmergencyContactMap v-if="tileCard.type === 'emergency-map'"
                                                    :location-name="tileCard.locationName || getCurrentTitle()"
                                                    :property-coordinates="selected_property?.coordinates"
                                                    :municipality="getPropertyMunicipality()"
                                                    :water-utility="getPropertyWaterUtility()"
                                                    :tariff-regime="getPropertyTariffRegime()"
                                                    :live-feed="selected_property?.is_live ? (tileCard.liveFeed || selected_property.liveFeed) : null"
                                                    :selected-property="selected_property" />
                                            </template>

                                            <SecurityDemographicsCompetition :crimeStats="currentCrimeStats" />
                                        </div>
                                    </template>
                                </template>

                                <!-- ⭐ RESOURCES LAYOUT -->
                                <template v-else>
                                    <div class="resources-team-section">
                                        <div class="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3 mb-4">
                                            <div>
                                                <h6 class="fw-semibold  mb-1">{{ resources_team_title }}</h6>
                                                <p class="text-secondary mb-0 text-sm">
                                                    {{ selected_property?.title || "Selected property" }}
                                                </p>
                                            </div>

                                            <span class="badge rounded-pill settings-soft-badge align-self-start align-self-md-center">
                                                {{ resources_team_members.length }} member{{ resources_team_members.length === 1 ? "" : "s" }}
                                            </span>
                                        </div>

                                        <div v-if="resources_team_members.length" class="row g-4">
                                            <div
                                                v-for="member in resources_team_members"
                                                :key="`${member.full_name}-${member.role}`"
                                                class="col-md-6 col-xl-4"
                                            >
                                                <article class="card bcx-card resources-team-card h-100 p-4">
                                                    <div class="d-flex align-items-center gap-3 mb-4">
                                                        <div class="resources-team-avatar d-flex align-items-center justify-content-center">
                                                            <span>{{ get_member_initials(member.full_name) }}</span>
                                                        </div>

                                                        <div class="min-w-0">
                                                            <p class=" fw-semibold mb-1 text-xs text-truncate">
                                                                {{ member.full_name }}
                                                            </p>
                                                            <p class="text-secondary mb-0 text-xs">{{ member.role }}</p>
                                                        </div>
                                                    </div>

                                                    <div class="d-flex align-items-center gap-2 mb-3">
                                                        <span class="status-dot" :class="get_member_status_class(member.status)"></span>
                                                        <span class="text-secondary text-xs">{{ get_member_status_label(member.status) }}</span>
                                                    </div>

                                                    <div class="d-flex flex-column gap-3">
                                                        <div v-if="member.hours" class="d-flex align-items-center">
                                                            <div class="icon-box me-3">
                                                                <i class="bi bi-clock-fill "></i>
                                                            </div>
                                                            <span class="text-secondary text-xs">{{ member.hours }}</span>
                                                        </div>

                                                        <div v-if="member.phone && member.phone !== 'N/A'" class="d-flex align-items-center">
                                                            <div class="icon-box me-3">
                                                                <i class="bi bi-telephone-fill "></i>
                                                            </div>
                                                            <span class="text-secondary text-xs">{{ member.phone }}</span>
                                                        </div>

                                                        <div v-if="member.email && member.email !== 'N/A'" class="d-flex align-items-center">
                                                            <div class="icon-box me-3">
                                                                <i class="bi bi-envelope-fill "></i>
                                                            </div>
                                                            <span class="text-secondary text-xs text-break">{{ member.email }}</span>
                                                        </div>
                                                    </div>
                                                </article>
                                            </div>
                                        </div>

                                        <div v-else class="card bcx-card text-center p-4">
                                            <h5 class=" fw-bold mb-1">No team members available</h5>
                                            <p class="text-secondary text-xs mb-0">
                                                Team members will appear here once they are linked to this property.
                                            </p>
                                        </div>
                                    </div>
                                    <!-- ROW 1 — DETAILS + RECENT DOCUMENTS -->
                                    <div v-if="false" class="row g-4 mb-4">
                                        <div class="col-lg-6">
                                            <div class="card bcx-card h-100">
                                                <div class="card-body">
                                                    <p class="text-xs ">
                                                        <i class="bi bi-people-fill text-danger fs-5"></i>
                                                        Team Members
                                                    </p>

                                                    <template v-for="tileCard in getTileCards()"
                                                        :key="'resources-details-' + tileCard.id">
                                                        <div v-if="tileCard.type === 'details'">
                                                            <DetailsCard v-for="(contact, index) in tileCard.contacts"
                                                                :key="`${tileCard.id}-${index}`"
                                                                :accordion_id="`${tileCard.id}-${index}`"
                                                                :full_name="contact.full_name" :role="contact.role"
                                                                :status="contact.status" :hours="contact.hours"
                                                                :address="contact.address" :website="contact.website"
                                                                :phone="contact.phone" :email="contact.email" />
                                                        </div>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    <!-- ROW 2 — SHIFT SCHEDULE + QUICK ACTIONS -->
                                    <div class="row g-4 mb-4 d-none">
                                        <div class="col-lg-6">
                                            <ShiftSchedule v-if="selected_property"
                                                :shifts="selected_property.shiftSchedule || []" />
                                        </div>

                                        <div class="col-lg-6">
                                            <QuickActions v-if="selected_property"
                                                :actions="selected_property.quickActions || []" />
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- RIGHT SIDEBAR / EXTRA PANELS -->
                    <div v-if="hasTileCards() && showRightPanel">
                        <template v-for="tileCard in getTileCards()" :key="tileCard.id">
                            <!-- render right-side widgets here when needed -->
                        </template>
                    </div>
                </div>

                <!-- ============== DETAIL VIEW ============== -->
                <div v-else-if="isDetailView(currentView)" class="dashboard-detail-view">
                    <div class="card rounded-3 bg-transparent border mt-4 mb-4">
                        <div class="portfolio-chart">
                            <div class="superset-embedded-container m-0 p-0">
                                <div :id="'dashboard-' + currentView"></div>

                                <div v-if="iframeLoading" class="iframe-loading">
                                    <div class="spinner-border spinner-border-sm me-2"></div>
                                    Loading embedded dashboard...

                                    <div v-if="iframeError" class="error-message mt-2 text-danger">
                                        Failed to load dashboard. Please try refreshing.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    </div>
</div>
